import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import { getAlertPreferences, updateAlertPreferences, getAlertHistory, testSendAlert } from '../lib/api';
import { formatDateTime } from '../lib/utils';
import { toast } from 'sonner';
import {
  Bell,
  Mail,
  MessageSquare,
  Phone,
  Settings,
  History,
  TestTube,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Loader2,
} from 'lucide-react';

export default function AlertSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testingSms, setTestingSms] = useState(false);
  const [preferences, setPreferences] = useState({
    email_alerts: true,
    sms_alerts: false,
    alert_thresholds: [50, 20, 10, 5, 0]
  });
  const [phone, setPhone] = useState('');
  const [alertHistory, setAlertHistory] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [prefsRes, historyRes] = await Promise.all([
        getAlertPreferences(),
        getAlertHistory(10)
      ]);
      
      setPreferences(prefsRes.data.preferences || {
        email_alerts: true,
        sms_alerts: false,
        alert_thresholds: [50, 20, 10, 5, 0]
      });
      setPhone(prefsRes.data.phone || '');
      setAlertHistory(historyRes.data.alerts || []);
    } catch (error) {
      toast.error('Failed to load alert settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateAlertPreferences({
        ...preferences,
        phone: phone || null
      });
      toast.success('Alert settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestAlert = async (type) => {
    try {
      if (type === 'email') {
        setTestingEmail(true);
      } else {
        setTestingSms(true);
      }
      
      await testSendAlert(type);
      toast.success(`Test ${type} alert sent!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to send test ${type}`);
    } finally {
      setTestingEmail(false);
      setTestingSms(false);
    }
  };

  const toggleThreshold = (threshold) => {
    const current = preferences.alert_thresholds || [];
    const newThresholds = current.includes(threshold)
      ? current.filter(t => t !== threshold)
      : [...current, threshold].sort((a, b) => b - a);
    
    setPreferences({ ...preferences, alert_thresholds: newThresholds });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Card className="border-0 shadow-md">
          <CardContent className="p-6">
            <Skeleton className="h-48 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="alert-settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-jakarta font-bold text-slate-900">Alert Settings</h1>
        <p className="text-slate-500 mt-1">Configure how you want to be notified about low minutes</p>
      </div>

      {/* Notification Channels */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-blue-600" />
            Notification Channels
          </CardTitle>
          <CardDescription>Choose how you want to receive alerts</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Email Alerts */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900">Email Alerts</p>
                <p className="text-sm text-slate-500">Receive alerts via email</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={preferences.email_alerts}
                onCheckedChange={(checked) => setPreferences({ ...preferences, email_alerts: checked })}
                data-testid="email-alerts-switch"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleTestAlert('email')}
                disabled={testingEmail || !preferences.email_alerts}
              >
                {testingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <TestTube className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          <Separator />

          {/* SMS Alerts */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">SMS Alerts</p>
                  <p className="text-sm text-slate-500">Receive alerts via text message</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={preferences.sms_alerts}
                  onCheckedChange={(checked) => setPreferences({ ...preferences, sms_alerts: checked })}
                  data-testid="sms-alerts-switch"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleTestAlert('sms')}
                  disabled={testingSms || !preferences.sms_alerts || !phone}
                >
                  {testingSms ? <Loader2 className="w-4 h-4 animate-spin" /> : <TestTube className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            {preferences.sms_alerts && (
              <div className="ml-14 space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <div className="relative max-w-xs">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+61 400 000 000"
                    className="pl-10"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    data-testid="phone-input"
                  />
                </div>
                <p className="text-xs text-slate-500">Include country code (e.g., +61 for Australia, +1 for US)</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Alert Thresholds */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Alert Thresholds
          </CardTitle>
          <CardDescription>Choose when to receive low minutes warnings</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {[50, 20, 10, 5, 0].map((threshold) => {
              const isSelected = (preferences.alert_thresholds || []).includes(threshold);
              return (
                <button
                  key={threshold}
                  onClick={() => toggleThreshold(threshold)}
                  className={`px-4 py-2 rounded-lg border-2 transition-all ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                  }`}
                  data-testid={`threshold-${threshold}`}
                >
                  {threshold === 0 ? (
                    <span className="flex items-center gap-2">
                      <XCircle className="w-4 h-4" />
                      Depleted (0%)
                    </span>
                  ) : (
                    <span>{threshold}% remaining</span>
                  )}
                </button>
              );
            })}
          </div>
          <p className="text-sm text-slate-500 mt-4">
            You'll receive one alert per threshold per billing cycle
          </p>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="save-alerts-btn"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Settings className="w-4 h-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </div>

      {/* Alert History */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-slate-600" />
            Recent Alerts
          </CardTitle>
        </CardHeader>
        <CardContent>
          {alertHistory.length === 0 ? (
            <div className="text-center py-8">
              <Bell className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No alerts sent yet</p>
              <p className="text-sm text-slate-400 mt-1">
                You'll see your alert history here
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {alertHistory.map((alert) => (
                <div
                  key={alert.alert_id}
                  className="flex items-center justify-between p-3 rounded-lg border border-slate-100"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      alert.threshold === 0 ? 'bg-red-100' : 'bg-amber-100'
                    }`}>
                      {alert.threshold === 0 ? (
                        <XCircle className="w-4 h-4 text-red-600" />
                      ) : (
                        <AlertTriangle className="w-4 h-4 text-amber-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">
                        {alert.threshold === 0 ? 'Minutes Depleted' : `${alert.threshold}% Minutes Remaining`}
                      </p>
                      <p className="text-sm text-slate-500">
                        {formatDateTime(alert.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {alert.email_sent && (
                      <Badge variant="outline" className="text-xs">
                        <Mail className="w-3 h-3 mr-1" />
                        Email
                      </Badge>
                    )}
                    {alert.sms_sent && (
                      <Badge variant="outline" className="text-xs">
                        <MessageSquare className="w-3 h-3 mr-1" />
                        SMS
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
